import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { retrieveCheckoutSession, SUBSCRIPTION_PLANS } from '../lib/stripe-checkout';
import { CheckCircle, Loader, AlertCircle } from 'lucide-react';
import GTMService from '../lib/gtm';

interface PaymentStatus {
  status: 'loading' | 'success' | 'error' | 'no_session';
  message: string;
  planName?: string;
  sessionId?: string;
  isNewUser?: boolean;
  redirectDestination?: string;
}

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateSubscriptionPlan, isLoading: authLoading, session, refreshSession } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>({
    status: 'loading',
    message: 'Verifying your payment...'
  });
  const [sessionRestoreAttempted, setSessionRestoreAttempted] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userTypeCheckRef = useRef<{ isNewUser: boolean; checked: boolean }>({ isNewUser: true, checked: false });
  const paymentVerificationRef = useRef<{ sessionId: string | null; inProgress: boolean }>({ sessionId: null, inProgress: false });

  // Function to attempt session restoration 
  const attemptSessionRestore = useCallback(async () => {
    if (sessionRestoreAttempted) return false;
    
    setSessionRestoreAttempted(true);
    console.log('🔄 PaymentSuccessPage: Attempting to restore user session after Stripe checkout...');
    
    try {
      // Use the built-in refreshSession method
      const restoredSession = await refreshSession();
      
      if (restoredSession) {
        console.log('✅ PaymentSuccessPage: Session restored successfully:', restoredSession.user.email);
        // Reduced delay to speed up the flow
        await new Promise(resolve => setTimeout(resolve, 800));
        return true;
      } else {
        console.log('⚠️ PaymentSuccessPage: No session found to restore');
        return false;
      }
    } catch (error) {
      console.error('❌ PaymentSuccessPage: Error during session restoration:', error);
      return false;
    }
  }, [sessionRestoreAttempted, refreshSession]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    console.log('🔄 PaymentSuccessPage useEffect triggered:', {
      sessionId,
      hasUser: !!user,
      hasSession: !!session,
      authLoading,
      sessionRestoreAttempted,
      currentPath: window.location.pathname + window.location.search
    });
    
    if (!sessionId) {
      console.log('❌ No session_id found in URL');
      setPaymentStatus({
        status: 'no_session',
        message: 'No payment session found. Please try again or contact support if you completed a payment.'
      });
      return;
    }

    // If auth is still loading, wait
    if (authLoading && !sessionRestoreAttempted) {
      console.log('⏳ Auth still loading, waiting...');
      return;
    }

    // If no user and we haven't attempted session restore yet, try to restore
    if (!user && !sessionRestoreAttempted) {
      console.log('🔄 No user found, attempting session restoration...');
      setPaymentStatus({
        status: 'loading',
        message: 'Restoring your session...'
      });
      attemptSessionRestore().then((restored) => {
        if (!restored) {
          // Session restoration failed, redirect to login
          console.log('🔒 Session restoration failed, redirecting to login');
          localStorage.setItem('rulevia_payment_session_id', sessionId);
          const currentPath = window.location.pathname + window.location.search;
          console.log('🔗 Storing session and redirecting to login:', { sessionId, returnTo: currentPath });
          navigate(`/onboarding?mode=login&returnTo=${encodeURIComponent(currentPath)}`, { replace: true });
        }
        // If restored, the auth state change will trigger another useEffect run
      });
      return;
    }

    // If we still don't have a user after attempting restore, redirect to login
    if (!user && sessionRestoreAttempted) {
      console.log('🔒 No user after session restore attempt, redirecting to login');
      localStorage.setItem('rulevia_payment_session_id', sessionId);
      const currentPath = window.location.pathname + window.location.search;
      console.log('🔗 Storing session and redirecting to login:', { sessionId, returnTo: currentPath });
      navigate(`/onboarding?mode=login&returnTo=${encodeURIComponent(currentPath)}`, { replace: true });
      return;
    }

    // If we have a user, proceed with payment verification
    if (user) {
      console.log('✅ User found, proceeding with payment verification');
      handlePaymentVerification(sessionId);
    }
  }, [searchParams, user, session, authLoading, navigate, sessionRestoreAttempted, attemptSessionRestore]);

  // Helper function to check if purchase has already been tracked
  const hasAlreadyTrackedPurchase = (sessionId: string): boolean => {
    const trackedPurchases = localStorage.getItem('rulevia_tracked_purchases');
    if (!trackedPurchases) return false;
    
    try {
      const tracked = JSON.parse(trackedPurchases);
      return tracked.includes(sessionId);
    } catch {
      return false;
    }
  };

  // Helper function to mark purchase as tracked
  const markPurchaseAsTracked = (sessionId: string): void => {
    const trackedPurchases = localStorage.getItem('rulevia_tracked_purchases');
    let tracked: string[] = [];
    
    if (trackedPurchases) {
      try {
        tracked = JSON.parse(trackedPurchases);
      } catch {
        tracked = [];
      }
    }
    
    if (!tracked.includes(sessionId)) {
      tracked.push(sessionId);
      // Keep only last 50 session IDs to prevent localStorage bloat
      if (tracked.length > 50) {
        tracked = tracked.slice(-50);
      }
      localStorage.setItem('rulevia_tracked_purchases', JSON.stringify(tracked));
    }
  };

  const handlePaymentVerification = async (sessionId: string) => {
    // Prevent duplicate verification attempts for the same session
    if (paymentVerificationRef.current.inProgress && paymentVerificationRef.current.sessionId === sessionId) {
      console.log('⚠️ PaymentSuccessPage: Payment verification already in progress for session:', sessionId);
      return;
    }
    
    paymentVerificationRef.current = { sessionId, inProgress: true };
    
    try {
      console.log('🔍 PaymentSuccessPage: Starting payment verification for session:', sessionId);

      // Retrieve and verify the checkout session from Stripe
      const sessionData = await retrieveCheckoutSession(sessionId);

      if (sessionData.payment_status !== 'paid') {
        throw new Error(`Payment not completed. Status: ${sessionData.payment_status}`);
      }

      const planName = sessionData.metadata.plan_name;
      const userId = sessionData.metadata.user_id;

      if (!user || userId !== user.id) {
        throw new Error('Session user mismatch. Please contact support.');
      }

      console.log('✅ Payment verified for plan:', planName);

      // Track successful purchase immediately (but only once per session)
      const planData = Object.values(SUBSCRIPTION_PLANS).find(p => p.name === planName);
      if (planData && user?.id) {
        if (hasAlreadyTrackedPurchase(sessionId)) {
          console.log('⚠️ GTM purchase event already tracked for session:', sessionId, '- skipping duplicate');
        } else {
          console.log('🛒 Tracking GTM purchase event (first time):', { sessionId, planName, price: planData.price });
          GTMService.trackPurchase(
            sessionId,
            planName,
            planData.price,
            user.id,
            'new'
          );
          markPurchaseAsTracked(sessionId);
          console.log('✅ GTM purchase event tracked and marked as complete');
        }
      }

      // Wait for webhook to create subscription
      setPaymentStatus({
        status: 'loading',
        message: 'Activating your subscription...',
        sessionId: sessionId,
        planName: planName
      });

      let attempts = 0;
      const maxAttempts = 10;
      let subscriptionActivated = false;

      while (attempts < maxAttempts && !subscriptionActivated) {
        console.log(`🔄 Checking subscription activation (attempt ${attempts + 1}/${maxAttempts})...`);
        
        const { data: activeSubscription, error: subError } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!subError && activeSubscription) {
          console.log('🎉 Subscription activated by webhook:', activeSubscription);
          subscriptionActivated = true;
          
          // Update user's subscription_plan field
          const { error: userUpdateError } = await supabase
            .from('users')
            .update({ 
              subscription_plan: activeSubscription.plan_id
            })
            .eq('id', user.id);

          if (userUpdateError) {
            console.error('⚠️ Failed to update user subscription plan:', userUpdateError);
          }

          // Update AuthContext
          try {
            await updateSubscriptionPlan(activeSubscription.plan_id);
            console.log('✅ AuthContext updated successfully');
          } catch (authError) {
            console.error('⚠️ AuthContext update failed (non-critical):', authError);
          }

          // Determine if this is a new user (check if they have any completed analyses) - cache result
          let isNewUser = true;
          let redirectDestination = 'dashboard';
          
          if (!userTypeCheckRef.current.checked) {
            console.log('🔍 PaymentSuccessPage: Checking if user is new (first time)');
            const { data: analysisCount } = await supabase
              .from('analyses')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', user.id)
              .eq('status', 'completed');

            isNewUser = (analysisCount || 0) === 0;
            userTypeCheckRef.current = { isNewUser, checked: true };
            console.log('✅ PaymentSuccessPage: User type determined:', { isNewUser, analysisCount });
          } else {
            isNewUser = userTypeCheckRef.current.isNewUser;
            console.log('📋 PaymentSuccessPage: Using cached user type:', { isNewUser });
          }
          
          redirectDestination = isNewUser ? 'dashboard' : 'credits';

          setPaymentStatus({
            status: 'success',
            message: `Payment successful! Your ${planName} subscription is now active.`,
            planName: planName,
            sessionId: sessionId,
            isNewUser: isNewUser,
            redirectDestination: redirectDestination
          });

          // Auto-redirect after 3 seconds (prevent duplicate navigation)
          if (!hasNavigated) {
            setHasNavigated(true);
            navigationTimeoutRef.current = setTimeout(() => {
              if (isNewUser) {
                console.log('🚀 PaymentSuccessPage: Redirecting new user to dashboard');
                navigate('/dashboard?onboarding=true', { replace: true });
              } else {
                console.log('🚀 PaymentSuccessPage: Redirecting existing user to credits page');
                navigate('/credits', { replace: true });
              }
            }, 3000);
          }

          break;
        }

        // Wait 1 second before next check
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
      }

      // If webhook didn't activate subscription, create it manually
      if (!subscriptionActivated) {
        await createSubscriptionManually(sessionData, planName, user.id);
      }

      // Clean up stored session data
      localStorage.removeItem('rulevia_payment_session_id');
      
    } catch (error) {
      console.error('❌ Payment verification error:', error);
      setPaymentStatus({
        status: 'error',
        message: `Failed to activate subscription: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
        sessionId: sessionId
      });
    } finally {
      // Reset verification state
      paymentVerificationRef.current = { sessionId: null, inProgress: false };
    }
  };


  const createSubscriptionManually = async (sessionData: any, planName: string, userId: string) => {
    console.log('⚠️ Creating subscription manually as fallback...');
    
    setPaymentStatus(prev => ({
      ...prev,
      status: 'loading',
      message: 'Activating subscription manually...'
    }));

    const planEntry = Object.entries(SUBSCRIPTION_PLANS).find(([, plan]) => 
      plan.name === planName
    );
    const planId = planEntry ? planEntry[0] : 'starter';

    const stripeSubscriptionId = sessionData.subscription || sessionData.id;
    const stripeCustomerId = sessionData.customer;

    // Create subscription
    const { data: subscriptionData, error: subscriptionError } = await supabase
      .from('user_subscriptions')
      .upsert({
        user_id: userId,
        stripe_subscription_id: stripeSubscriptionId,
        stripe_customer_id: stripeCustomerId,
        plan_id: planId,
        status: 'active',
        current_period_start: new Date().toISOString(),
        current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      }, {
        onConflict: 'user_id'
      })
      .select()
      .single();

    if (subscriptionError) {
      throw new Error(`Failed to activate subscription: ${subscriptionError.message}`);
    }

    // Update user subscription_plan
    await supabase
      .from('users')
      .update({ subscription_plan: planId })
      .eq('id', userId);

    // Create subscription limits
    await supabase
      .from('subscription_limits')
      .upsert({
        user_id: userId,
        current_analyses_used: 0,
        current_team_seats_used: 1,
        current_projects_count: 0,
        current_versions_used: 0,
        current_period_start: new Date().toISOString(),
        last_rollover_date: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });

    // Update AuthContext
    try {
      await updateSubscriptionPlan(planId);
    } catch (authError) {
      console.error('⚠️ AuthContext update failed (non-critical):', authError);
    }

    console.log('✅ Subscription created manually:', subscriptionData);
    
    // Track purchase for manual subscription creation (if not already tracked)
    const sessionId = paymentStatus.sessionId || 'manual_fallback';
    if (sessionId && !hasAlreadyTrackedPurchase(sessionId)) {
      const planData = Object.values(SUBSCRIPTION_PLANS).find(p => p.name === planName);
      if (planData) {
        console.log('🛒 Tracking GTM purchase event for manual subscription:', { sessionId, planName, price: planData.price });
        GTMService.trackPurchase(
          sessionId,
          planName,
          planData.price,
          userId,
          'new'
        );
        markPurchaseAsTracked(sessionId);
        console.log('✅ GTM purchase event tracked for manual subscription');
      }
    } else {
      console.log('⚠️ GTM purchase event already tracked for manual subscription - skipping duplicate');
    }
    
    // Determine redirect destination (use cached result if available)
    let isNewUser = true;
    let redirectDestination = 'dashboard';
    
    if (!userTypeCheckRef.current.checked) {
      console.log('🔍 PaymentSuccessPage: Checking if user is new (manual fallback)');
      const { data: analysisCount } = await supabase
        .from('analyses')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'completed');

      isNewUser = (analysisCount || 0) === 0;
      userTypeCheckRef.current = { isNewUser, checked: true };
      console.log('✅ PaymentSuccessPage: User type determined (manual):', { isNewUser, analysisCount });
    } else {
      isNewUser = userTypeCheckRef.current.isNewUser;
      console.log('📋 PaymentSuccessPage: Using cached user type (manual):', { isNewUser });
    }
    
    redirectDestination = isNewUser ? 'dashboard' : 'credits';

    setPaymentStatus({
      status: 'success',
      message: `Payment successful! Your ${planName} subscription is now active.`,
      planName: planName,
      sessionId: paymentStatus.sessionId,
      isNewUser: isNewUser,
      redirectDestination: redirectDestination
    });

    // Auto-redirect after 3 seconds (prevent duplicate navigation)
    if (!hasNavigated) {
      setHasNavigated(true);
      navigationTimeoutRef.current = setTimeout(() => {
        if (isNewUser) {
          console.log('🚀 PaymentSuccessPage: Redirecting new user to dashboard (manual fallback)');
          navigate('/dashboard?onboarding=true', { replace: true });
        } else {
          console.log('🚀 PaymentSuccessPage: Redirecting existing user to credits page (manual fallback)');
          navigate('/credits', { replace: true });
        }
      }, 3000);
    }
  };

  const renderContent = () => {
    switch (paymentStatus.status) {
      case 'loading':
        return (
          <div className="text-center">
            <Loader className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h1>
            <p className="text-gray-600">{paymentStatus.message}</p>
            {paymentStatus.sessionId && (
              <p className="text-xs text-gray-400 mt-2">Session: {paymentStatus.sessionId}</p>
            )}
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
            <p className="text-gray-600 mb-4">{paymentStatus.message}</p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800 font-medium">What's Next?</p>
              <ul className="text-green-700 text-sm mt-2 list-disc list-inside">
                <li>Your {paymentStatus.planName} subscription is now active</li>
                <li>You have full access to your plan features</li>
                <li>Start analyzing your creative content</li>
                <li>
                  Redirecting to {paymentStatus.isNewUser ? 'dashboard' : 'credits page'} in a moment...
                </li>
              </ul>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => {
                  if (paymentStatus.isNewUser) {
                    navigate('/dashboard?onboarding=true', { replace: true });
                  } else {
                    navigate('/credits', { replace: true });
                  }
                }}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {paymentStatus.isNewUser ? 'Go to Dashboard' : 'Go to Credits'}
              </button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Activation Issue</h1>
            <p className="text-gray-600 mb-4">{paymentStatus.message}</p>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800 font-medium">Need Help?</p>
              <ul className="text-red-700 text-sm mt-2 list-disc list-inside">
                <li>Your payment was processed successfully</li>
                <li>We're working to activate your subscription</li>
                <li>Contact support with your session ID: {paymentStatus.sessionId}</li>
                <li>We'll resolve this within 24 hours</li>
              </ul>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => window.location.href = 'mailto:support@rulevia.com?subject=Payment%20Activation%20Issue&body=Session%20ID:%20' + paymentStatus.sessionId}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Contact Support
              </button>
            </div>
          </div>
        );

      case 'no_session':
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">No Payment Session</h1>
            <p className="text-gray-600 mb-4">{paymentStatus.message}</p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/onboarding?step=subscription')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Choose Subscription Plan
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        console.log('🧹 PaymentSuccessPage: Cleaned up navigation timeout');
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Payment Successful - Rulevia</title>
      </Helmet>
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
} 